/**
 * Client-side read queries (via anon key + RLS).
 * These run in the browser only — public routes use ssr:false so loaders
 * execute after hydration.
 */
import { queryOptions } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { loadArchive } from "./archive";

function sortLocs<T extends { video_locations?: Array<{ is_primary?: boolean }> }>(rows: T[]): T[] {
  for (const r of rows) {
    if (Array.isArray(r.video_locations)) {
      r.video_locations.sort((a, b) => Number(!!b.is_primary) - Number(!!a.is_primary));
    }
  }
  return rows;
}

export const youtubersListQuery = queryOptions({
  queryKey: ["youtubers"],
  queryFn: async () => {
    const { data, error } = await supabase
      .from("youtubers")
      .select("*")
      .eq("hidden", false)
      .order("subscriber_count", { ascending: false });
    if (error) throw error;
    return data ?? [];
  },
});

export const youtuberByHandleQuery = (handle: string) =>
  queryOptions({
    queryKey: ["youtuber", handle],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("youtubers")
        .select("*")
        .eq("handle", handle)
        .eq("hidden", false)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });

export interface VideoWithLocations {
  id: string;
  slug?: string | null;
  youtuber_id: string;
  youtube_video_id: string;
  title: string;
  description: string | null;
  thumbnail_url: string | null;
  published_at: string | null;
  duration_seconds: number | null;
  view_count: number | null;
  like_count: number | null;
  is_travel: boolean;
  hidden: boolean;
  geolocated: boolean;
  description_clean: string | null;
  transcript_summary: string | null;
  video_locations: Array<{
    id: string;
    name: string | null;
    country_code: string | null;
    country_name: string | null;
    region: string | null;
    city: string | null;
    latitude: number | null;
    longitude: number | null;
    is_primary: boolean;
    source: string;
  }>;
  youtubers?: { handle: string; name: string; avatar_url: string | null; creator_id?: string | null; creators?: { slug: string } | null } | null;
}

export const videosByYoutuberQuery = (youtuberId: string) =>
  queryOptions({
    queryKey: ["videos", "by-youtuber", youtuberId],
    queryFn: async (): Promise<VideoWithLocations[]> => {
      { const a = await loadArchive(); if (a) return a.byYoutuber.get(youtuberId) ?? []; }
      // Slim projection — the public youtuber page never renders description,
      // description_clean, transcript, or transcript_summary. Skipping those
      // large text columns keeps the payload small and the page snappy.
      const { data, error } = await supabase
        .from("videos")
        .select(
          "id, slug, youtuber_id, youtube_video_id, title, thumbnail_url, published_at, duration_seconds, view_count, like_count, is_travel, hidden, geolocated, video_locations(*)",
        )
        .eq("youtuber_id", youtuberId)
        .eq("hidden", false)
        .eq("deleted", false)
        .order("published_at", { ascending: false });
      if (error) throw error;
      return sortLocs((data ?? []) as unknown as VideoWithLocations[]);
    },
  });
export const videoByIdQuery = (id: string) =>
  queryOptions({
    queryKey: ["video", id],
    queryFn: async (): Promise<VideoWithLocations | null> => {
      const { data, error } = await supabase
        .from("videos")
        .select("*, video_locations(*), youtubers!inner(handle, name, avatar_url)")
        .eq("id", id)
        .eq("youtubers.hidden", false)
        .maybeSingle();
      if (error) throw error;
      if (data) sortLocs([data as VideoWithLocations]);
      return data as VideoWithLocations | null;
    },
  });


/**
 * Resolve /v/:slug. Accepts a friendly slug (title-channel-date) or, for
 * legacy links, a raw video UUID.
 */
export const videoBySlugQuery = (slug: string) =>
  queryOptions({
    queryKey: ["video", "bySlug", slug],
    queryFn: async (): Promise<VideoWithLocations | null> => {
      const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(slug);
      const { data, error } = await supabase
        .from("videos")
        .select("*, video_locations(*), youtubers!inner(handle, name, avatar_url, creator_id, creators(slug))")
        .eq(isUuid ? "id" : "slug", slug)
        .eq("youtubers.hidden", false)
        .maybeSingle();
      if (error) throw error;
      if (data) sortLocs([data as VideoWithLocations]);
      return data as VideoWithLocations | null;
    },
  });

/** Canonical URL param for a video: friendly slug when available. */
export const videoParam = (v: { id: string; slug?: string | null }) => v.slug || v.id;

export const recentVideosQuery = queryOptions({
  queryKey: ["videos", "recent"],
  queryFn: async (): Promise<VideoWithLocations[]> => {
      { const a = await loadArchive(); if (a) return a.videos.slice(0, 24); }
    const { data, error } = await supabase
      .from("videos")
      .select("*, video_locations(*), youtubers!inner(handle, name, avatar_url)")
      .eq("youtubers.hidden", false)
      .eq("hidden", false)
        .eq("deleted", false)
      .order("published_at", { ascending: false })
      .limit(24);
    if (error) throw error;
    return sortLocs((data ?? []) as VideoWithLocations[]);
  },
});

export function primaryLocation(v: VideoWithLocations) {
  return v.video_locations.find((l) => l.is_primary) ?? v.video_locations[0] ?? null;
}

// Lightweight aggregate: all non-hidden videos with youtuber_id + locations.
// Used to compute top countries and per-youtuber stats without pulling full rows.
export interface AggregateVideo {
  id: string;
  youtuber_id: string;
  view_count: number | null;
  video_locations: Array<{
    country_code: string | null;
    country_name: string | null;
    is_primary: boolean;
  }>;
}

/** Compact per-video row for the map: one request, primary location only. */
export interface MapPinRow {
  id: string;
  slug: string | null;
  title: string;
  ytId: string;
  year: number | null;
  youtuber_id: string;
  creator_id: string | null;
  views: number;
  lat: number | null;
  lng: number | null;
  cc: string | null;
  cn: string | null;
  city: string | null;
}

export const mapPinsQuery = queryOptions({
  queryKey: ["map-pins"],
  queryFn: async (): Promise<MapPinRow[]> => {
      { const a = await loadArchive(); if (a) return a.videos.map((v) => { const l = v.video_locations[0]; return { id: v.id, slug: v.slug ?? null, title: v.title, ytId: v.youtube_video_id, year: v.published_at ? Number(v.published_at.slice(0, 4)) : null, youtuber_id: v.youtuber_id, creator_id: v.youtubers?.creator_id ?? null, views: v.view_count ?? 0, lat: l?.latitude ?? null, lng: l?.longitude ?? null, cc: l?.country_code ?? null, cn: l?.country_name ?? null, city: l?.city ?? null }; }); }
    const { data, error } = await (supabase as any).rpc("map_pins");
    if (error) throw error;
    return ((data ?? []) as any[]).map((r) => ({
      id: r[0], slug: r[1], title: r[2], ytId: r[3], year: r[4], youtuber_id: r[5],
      creator_id: r[6], views: r[7], lat: r[8], lng: r[9], cc: r[10], cn: r[11], city: r[12],
    }));
  },
});

export const aggregatesQuery = queryOptions({
  queryKey: ["aggregates"],
  queryFn: async (): Promise<AggregateVideo[]> => {
    const rows = await mapPinsQuery.queryFn!({} as any) as MapPinRow[];
    return rows.map((r) => ({
      id: r.id,
      youtuber_id: r.youtuber_id,
      view_count: r.views,
      video_locations: r.cc ? [{ country_code: r.cc, country_name: r.cn, is_primary: true }] : [],
    }));
  },
});


export type CountryMetric = "videos" | "views" | "channels";

export interface CountryRow {
  code: string;
  name: string;
  videos: number;
  views: number;
  channels: number;
}

export function countriesFrom(rows: AggregateVideo[]): CountryRow[] {
  const map = new Map<string, { code: string; name: string; videos: number; views: number; channels: Set<string> }>();
  for (const v of rows) {
    const loc = v.video_locations.find((l) => l.is_primary) ?? v.video_locations[0];
    if (!loc?.country_code) continue;
    const key = loc.country_code;
    const entry = map.get(key) ?? { code: key, name: loc.country_name ?? key, videos: 0, views: 0, channels: new Set<string>() };
    entry.videos += 1;
    entry.views += v.view_count ?? 0;
    entry.channels.add(v.youtuber_id);
    map.set(key, entry);
  }
  return Array.from(map.values()).map((e) => ({
    code: e.code,
    name: e.name,
    videos: e.videos,
    views: e.views,
    channels: e.channels.size,
  }));
}

export function topCountriesFrom(
  rows: AggregateVideo[],
): Array<{ code: string; name: string; count: number }> {
  return countriesFrom(rows)
    .sort((a, b) => b.videos - a.videos)
    .map((r) => ({ code: r.code, name: r.name, count: r.videos }));
}


export function youtuberStatsFrom(
  rows: AggregateVideo[],
): Map<string, { videos: number; countries: number }> {
  const per = new Map<string, { videos: number; countries: Set<string> }>();
  for (const v of rows) {
    const e = per.get(v.youtuber_id) ?? { videos: 0, countries: new Set<string>() };
    e.videos += 1;
    const loc = v.video_locations.find((l) => l.is_primary) ?? v.video_locations[0];
    if (loc?.country_code) e.countries.add(loc.country_code);
    per.set(v.youtuber_id, e);
  }
  const out = new Map<string, { videos: number; countries: number }>();
  per.forEach((val, key) => out.set(key, { videos: val.videos, countries: val.countries.size }));
  return out;
}

export function creatorStatsFrom(
  rows: AggregateVideo[],
  creators: Array<{ id: string; channels: Array<{ id: string }> }>,
): Map<string, { videos: number; countries: number }> {
  const channelToCreator = new Map<string, string>();
  for (const c of creators) for (const ch of c.channels) channelToCreator.set(ch.id, c.id);
  const per = new Map<string, { videos: number; countries: Set<string> }>();
  for (const v of rows) {
    const cid = channelToCreator.get(v.youtuber_id);
    if (!cid) continue;
    const e = per.get(cid) ?? { videos: 0, countries: new Set<string>() };
    e.videos += 1;
    const loc = v.video_locations.find((l) => l.is_primary) ?? v.video_locations[0];
    if (loc?.country_code) e.countries.add(loc.country_code);
    per.set(cid, e);
  }
  const out = new Map<string, { videos: number; countries: number }>();
  per.forEach((val, key) => out.set(key, { videos: val.videos, countries: val.countries.size }));
  return out;
}

// Broader videos list for the /search page and home map. Slim projection —
// skip heavy text columns (description, description_clean, transcript,
// transcript_summary); none are rendered in list/map views.
export const allVideosQuery = queryOptions({
  queryKey: ["videos", "all-slim"],
  queryFn: async (): Promise<VideoWithLocations[]> => {
      { const a = await loadArchive(); if (a) return a.videos; }
    // PostgREST caps responses at 1000 rows — page through everything.
    const PAGE = 1000;
    const all: VideoWithLocations[] = [];
    for (let from = 0; ; from += PAGE) {
      const { data, error } = await supabase
        .from("videos")
        .select(
          "id, slug, youtuber_id, youtube_video_id, title, thumbnail_url, published_at, duration_seconds, view_count, is_travel, hidden, geolocated, video_locations(*), youtubers!inner(handle, name, avatar_url, creator_id)",
        )
        .eq("youtubers.hidden", false)
        .eq("hidden", false)
        .eq("deleted", false)
        .order("published_at", { ascending: false })
        .order("id", { ascending: true })
        .range(from, from + PAGE - 1);
      if (error) throw error;
      const rows = (data ?? []) as unknown as VideoWithLocations[];
      all.push(...rows);
      if (rows.length < PAGE) break;
    }
    return sortLocs(all);
  },
});


// ============= CREATORS (people) =============

export interface CreatorRow {
  id: string;
  slug: string;
  name: string;
  full_name: string | null;
  birth_date: string | null;
  bio: string | null;
  country: string | null;
  avatar_url: string | null;
}

export interface CreatorChannel {
  id: string;
  name: string;
  handle: string;
  avatar_url: string | null;
  subscriber_count: number | null;
  video_count: number | null;
  country: string | null;
  is_primary_channel: boolean;
  creator_id: string | null;
}

export const creatorsListQuery = queryOptions({
  queryKey: ["creators"],
  queryFn: async (): Promise<Array<CreatorRow & { channels: CreatorChannel[] }>> => {
    const [creatorsRes, channelsRes] = await Promise.all([
      (supabase as any).from("creators").select("*").order("name"),
      (supabase as any)
        .from("youtubers")
        .select("id, name, handle, avatar_url, subscriber_count, video_count, country, is_primary_channel, creator_id")
        .eq("hidden", false),
    ]);
    if (creatorsRes.error) throw creatorsRes.error;
    if (channelsRes.error) throw channelsRes.error;
    const byCreator = new Map<string, CreatorChannel[]>();
    for (const ch of (channelsRes.data ?? []) as CreatorChannel[]) {
      if (!ch.creator_id) continue;
      const arr = byCreator.get(ch.creator_id) ?? [];
      arr.push(ch);
      byCreator.set(ch.creator_id, arr);
    }
    return ((creatorsRes.data ?? []) as CreatorRow[])
      .map((c) => ({
        ...c,
        channels: (byCreator.get(c.id) ?? []).sort(
          (a, b) => Number(b.is_primary_channel) - Number(a.is_primary_channel) ||
            (b.subscriber_count ?? 0) - (a.subscriber_count ?? 0),
        ),
      }))
      // Hidden channels are filtered out by the database; a creator left with
      // no visible channel must not appear on the public site either.
      .filter((c) => c.channels.length > 0);
  },
});

/**
 * Resolve /y/:handle. `handle` may match either a creator.slug or a legacy
 * youtubers.handle. Returns the resolved creator (person) plus all its
 * channels. When the slug matches a channel with no creator, falls back to
 * a "virtual" single-channel creator so existing links keep working.
 */
export const creatorByHandleQuery = (handle: string) =>
  queryOptions({
    queryKey: ["creator", "byHandle", handle],
    queryFn: async (): Promise<{ creator: CreatorRow; channels: CreatorChannel[] } | null> => {
      // 1) Try creator.slug
      const cRes: any = await (supabase as any)
        .from("creators")
        .select("*")
        .eq("slug", handle)
        .maybeSingle();
      if (cRes.error) throw cRes.error;
      if (cRes.data) {
        const chRes: any = await (supabase as any)
          .from("youtubers")
          .select("id, name, handle, avatar_url, subscriber_count, video_count, country, is_primary_channel, creator_id")
          .eq("creator_id", cRes.data.id)
          .eq("hidden", false);
        if (chRes.error) throw chRes.error;
        const channels = ((chRes.data ?? []) as CreatorChannel[]).sort(
          (a, b) => Number(b.is_primary_channel) - Number(a.is_primary_channel) ||
            (b.subscriber_count ?? 0) - (a.subscriber_count ?? 0),
        );
        // Every channel hidden → treat the profile as non-existent publicly.
        if (channels.length === 0) return null;
        return { creator: cRes.data as CreatorRow, channels };
      }
      // 2) Fallback to channel handle for legacy links
      const yRes: any = await (supabase as any)
        .from("youtubers")
        .select("*")
        .eq("handle", handle)
        .eq("hidden", false)
        .maybeSingle();
      if (yRes.error) throw yRes.error;
      if (!yRes.data) return null;
      const y = yRes.data;
      return {
        creator: {
          id: y.id,
          slug: y.handle,
          name: y.name,
          full_name: null,
          birth_date: null,
          bio: y.description ?? null,
          country: y.country,
          avatar_url: y.avatar_url,
        },
        channels: [{
          id: y.id, name: y.name, handle: y.handle, avatar_url: y.avatar_url,
          subscriber_count: y.subscriber_count, video_count: y.video_count,
          country: y.country, is_primary_channel: true, creator_id: null,
        }],
      };
    },
  });

export const videosByChannelIdsQuery = (channelIds: string[]) =>
  queryOptions({
    queryKey: ["videos", "byChannels", ...[...channelIds].sort()],
    queryFn: async (): Promise<VideoWithLocations[]> => {
      { const a = await loadArchive(); if (a) { const set = new Set(channelIds); return a.videos.filter((v) => set.has(v.youtuber_id)); } }
      if (channelIds.length === 0) return [];
      const { data, error } = await supabase
        .from("videos")
        .select(
          "id, slug, youtuber_id, youtube_video_id, title, thumbnail_url, published_at, duration_seconds, view_count, like_count, is_travel, hidden, geolocated, video_locations(*)",
        )
        .in("youtuber_id", channelIds)
        .eq("hidden", false)
        .eq("deleted", false)
        .order("published_at", { ascending: false });
      if (error) throw error;
      return sortLocs((data ?? []) as unknown as VideoWithLocations[]);
    },
  });

/**
 * Candidate videos for the "related videos" section of a video page:
 * every non-hidden video that has a location in one of the given countries.
 * Ranking by geographic distance happens client-side.
 */
export const relatedCandidatesQuery = (countryCodes: string[]) =>
  queryOptions({
    queryKey: ["videos", "relatedCandidates", ...[...countryCodes].sort()],
    queryFn: async (): Promise<VideoWithLocations[]> => {
      { const a = await loadArchive(); if (a) { const set = new Set(countryCodes); return a.videos.filter((v) => v.video_locations.some((l) => l.country_code && set.has(l.country_code))).slice(0, 400); } }
      if (countryCodes.length === 0) return [];
      const { data, error } = await supabase
        .from("videos")
        .select(
          "id, slug, youtuber_id, youtube_video_id, title, thumbnail_url, published_at, duration_seconds, view_count, video_locations!inner(*), youtubers!inner(handle, name, avatar_url)",
        )
        .in("video_locations.country_code", countryCodes)
        .eq("youtubers.hidden", false)
        .eq("hidden", false)
        .eq("deleted", false)
        .order("published_at", { ascending: false })
        .limit(400);
      if (error) throw error;
      return sortLocs((data ?? []) as unknown as VideoWithLocations[]);
    },
  });

/**
 * Cheap freshness probe (a single timestamp). Public data is cached for
 * minutes, so a watcher polls this every 30s and invalidates the cache as soon
 * as anything changes in the backoffice — no minutes-long staleness, no
 * re-downloading everything on each navigation.
 */
export const contentRevisionQuery = queryOptions({
  queryKey: ["content-revision"],
  queryFn: async (): Promise<string> => {
    const { data, error } = await supabase.rpc("content_revision");
    if (error) throw error;
    return (data as unknown as string) ?? "";
  },
  staleTime: 30 * 1000,
  gcTime: 5 * 60 * 1000,
});

/**
 * Videos that have at least one location in a given country. Used by the
 * country page so it never has to download the whole catalogue.
 */
export const videosByCountryQuery = (countryCode: string) =>
  queryOptions({
    queryKey: ["videos", "byCountry", countryCode],
    queryFn: async (): Promise<VideoWithLocations[]> => {
      { const a = await loadArchive(); if (a) return a.videos.filter((v) => v.video_locations.some((l) => l.country_code === countryCode)); }
      const ids = new Set<string>();
      const PAGE = 1000;
      for (let from = 0; ; from += PAGE) {
        const { data, error } = await supabase
          .from("video_locations")
          .select("video_id")
          .eq("country_code", countryCode)
          .range(from, from + PAGE - 1);
        if (error) throw error;
        const rows = data ?? [];
        for (const r of rows) ids.add((r as { video_id: string }).video_id);
        if (rows.length < PAGE) break;
      }
      if (ids.size === 0) return [];

      const list = [...ids];
      const CHUNK = 200;
      const all: VideoWithLocations[] = [];
      for (let i = 0; i < list.length; i += CHUNK) {
        const { data, error } = await supabase
          .from("videos")
          .select(
            "id, slug, youtuber_id, youtube_video_id, title, thumbnail_url, published_at, duration_seconds, view_count, is_travel, hidden, geolocated, video_locations(*), youtubers!inner(handle, name, avatar_url, creator_id)",
          )
          .in("id", list.slice(i, i + CHUNK))
          .eq("youtubers.hidden", false)
          .eq("hidden", false)
          .eq("deleted", false)
          .order("published_at", { ascending: false });
        if (error) throw error;
        all.push(...((data ?? []) as unknown as VideoWithLocations[]));
      }
      all.sort((a, b) => ((a.published_at ?? "") < (b.published_at ?? "") ? 1 : -1));
      return sortLocs(all);
    },
  });

/**
 * Date of the newest video in the catalogue (any channel). Used to show a
 * "last updated" stamp on the site.
 */
export const lastUpdateQuery = queryOptions({
  queryKey: ["last-update"],
  queryFn: async (): Promise<string | null> => {
      { const a = await loadArchive(); if (a) return a.videos.find((v) => v.published_at)?.published_at ?? null; }
    const { data, error } = await supabase
      .from("videos")
      .select("published_at, youtubers!inner(hidden)")
      .eq("youtubers.hidden", false)
      .eq("hidden", false)
      .eq("deleted", false)
      .not("published_at", "is", null)
      .order("published_at", { ascending: false })
      .limit(1)
      .maybeSingle();
    if (error) throw error;
    return (data?.published_at as string | null) ?? null;
  },
  staleTime: 5 * 60 * 1000,
});

/** Public numbers shown on the About page ("the archive in numbers"). */
export interface SiteStats {
  videos: number;
  locations: number;
  youtubers: number;
  countries: number;
  gzipBytes: number | null;
  generatedAt: string | null;
}

export const siteStatsQuery = queryOptions({
  queryKey: ["site-stats"],
  queryFn: async (): Promise<SiteStats> => {
      { const a = await loadArchive();
        if (a) {
          const countries = new Set<string>();
          let locations = 0;
          for (const v of a.videos) {
            locations += v.video_locations.length;
            for (const l of v.video_locations) if (l.country_code) countries.add(l.country_code);
          }
          return {
            videos: a.videos.length,
            locations,
            youtubers: new Set(a.videos.map((v) => v.youtuber_id)).size,
            countries: countries.size,
            gzipBytes: a.gzipBytes,
            generatedAt: a.generatedAt,
          };
        } }
    const { data, error } = await (supabase as any).rpc("site_stats");
    if (error) throw error;
    return {
      videos: Number(data?.videos ?? 0),
      locations: Number(data?.locations ?? 0),
      youtubers: Number(data?.youtubers ?? 0),
      countries: Number(data?.countries ?? 0),
      gzipBytes: null,
      generatedAt: null,
    };
  },
  // The loader prefills the numbers from the database for SSR; the browser
  // always recomputes them from the archive so the file size/date show up.
  staleTime: 0,
  refetchOnMount: "always",
});
